import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import { useDropzone } from 'react-dropzone';
import Loading from '../../shared/loading';
import AutoCropImagePreview from './auto-crop-image-preview';
import { useUploadImage } from '../../services/wire/image-services';
import { useAssignPrimaryImage } from '../../services/wire/primary-image-services';

const MIN_WIDTH = 1280;
const MIN_HEIGHT = 720;

const buildOnDrop = (onError, onSuccess) => acceptedFiles => {
  const file = acceptedFiles[0];
  const image = new Image();
  const fileBlob = URL.createObjectURL(file);

  image.addEventListener('load', function onload() {
    URL.revokeObjectURL(fileBlob);
    if (this.naturalWidth < MIN_WIDTH || this.naturalHeight < MIN_HEIGHT) {
      onError(`The image is too small. Please upload an image ${MIN_WIDTH} x ${MIN_HEIGHT}px or larger.`);
    } else {
      onError(null);
      onSuccess({ ...file, content: URL.createObjectURL(file) });
    }
  });
  image.src = fileBlob;
};

const DropZone = ({ onError, onSuccess }) => {
  const {
    getRootProps, getInputProps, isDragActive, open,
  } = useDropzone({
    accept: 'image/*',
    multiple: false,
    noClick: true,
    onDrop: buildOnDrop(onError, onSuccess),
  });

  return (
    <section {...getRootProps({ className: `dropzone${isDragActive ? ' active' : ''}` })}>
      <input {...getInputProps()} />
      <div className="content">
        <svg className="icon"><use href="#upload" /></svg>
        <h6>
          {'Drag & drop image here or '}
          <a href="#" onClick={() => open()}>choose file</a>
        </h6>
      </div>
    </section>
  );
};

DropZone.propTypes = {
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const Header = () => (
  <Modal.Header>
    <div className="primary-image-modal-header">
      <Modal.Title as="h3">Assign Primary Image</Modal.Title>
      <p>
        Assigning an image to your wire ensures your wire always looks as
        good as the content you’ve written.
      </p>
    </div>
  </Modal.Header>
);

const Footer = ({ disabled, onCancel, onSubmit }) => (
  <Modal.Footer as="footer">
    <div className="btn-group">
      <button type="button" className="btn btn-secondary-inverted" onClick={onCancel}>
        Cancel
      </button>
      <button type="button" className="btn btn-primary" disabled={disabled} onClick={onSubmit}>
        Assign Primary Image
      </button>
    </div>
  </Modal.Footer>
);

Footer.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default function PrimaryImageModal({
  showModal, wireId, mediaUrl, onAssigned, onClose,
}) {
  const [assigned, setAssigned] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [preview, setPreview] = React.useState(mediaUrl);
  const loadingRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const [{ data: imageData, loading: uploadInProgress }, upload] = useUploadImage(wireId);
  const [{ response: assignRes, loading: asssignInProgress }, assign] = useAssignPrimaryImage();

  React.useEffect(() => {
    showModal && setAssigned(false);
  }, [showModal]);

  React.useEffect(() => {
    setPreview(showModal ? mediaUrl : null);
  }, [showModal, mediaUrl]);

  React.useEffect(() => () => (preview && URL.revokeObjectURL(preview.content)), [preview]);

  React.useEffect(() => {
    imageData && assign(wireId, imageData.files[0].id);
  }, [imageData, wireId, assign]);

  React.useEffect(() => {
    const valid = !assigned
      && loadingRef.current
      && _.get(assignRes, 'status') === 200
      && _.get(imageData, 'files[0].url');
    if (valid) {
      loadingRef.current.stop();
      onAssigned(imageData.files[0].url);
      onClose();
      return () => setAssigned(true);
    }
    return () => null;
  }, [assignRes, imageData, onAssigned, onClose, assigned]);

  React.useEffect(() => {
    if (uploadInProgress || asssignInProgress) {
      loadingRef.current.start();
    }
  }, [uploadInProgress, asssignInProgress]);

  async function handleAssign() {
    if (preview === mediaUrl) {
      onClose();
    } else {
      const blob = await previewRef.current.getOriginalCanvasBlob();
      upload(blob, preview.path);
    }
  }

  return (
    <Modal
      centered="true"
      dialogAs="article"
      show={showModal}
      backdrop="static"
      dialogClassName="primary-image-modal"
    >
      <Header />
      <Modal.Body as="section" className="vertical">
        <div className="primary-image-modal-body">
          <section className={`preview-box ${preview ? '' : 'empty'}`}>
            {
              preview
                ? (
                  <AutoCropImagePreview
                    ref={previewRef}
                    source={preview.content || preview}
                    aspect={16 / 9}
                    width={225}
                  />
                )
                : <h6 className="placeholder-text">Preview</h6>
            }
          </section>
          <div className="remove">
            {!!preview && (
              <React.Fragment>
                <Loading variant="inline" ref={loadingRef} />
                <a href="#" onClick={e => e.preventDefault() || setPreview(null)}>remove</a>
              </React.Fragment>
            )}
          </div>
          <h6 className="title">Upload Image</h6>
          <DropZone onError={setError} onSuccess={setPreview} />
          <section className="description">
            <span className="info-block">
              {`Mimimum Image Size: ${MIN_WIDTH} x ${MIN_HEIGHT}px`}
            </span>
            {!!error && <span className="help-block">{error}</span>}
          </section>
        </div>
      </Modal.Body>
      <Footer
        disabled={!preview || uploadInProgress || asssignInProgress}
        onCancel={onClose}
        onSubmit={handleAssign}
      />
    </Modal>
  );
}

PrimaryImageModal.propTypes = {
  wireId: PropTypes.number.isRequired,
  onAssigned: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showModal: PropTypes.bool,
  mediaUrl: PropTypes.string,
};

PrimaryImageModal.defaultProps = {
  showModal: false,
  mediaUrl: null,
};
